import { z } from 'zod'
import { oc } from '@orpc/contract'

const BRIDGE_PROVIDER_ID = /^prv_[A-Za-z0-9]+$/
const NOT_A_BRIDGE_ID = 'Expected a Bridge provider id (prv_…)'

/** The wire value: a Bridge provider id, or `null` to unlink. */
export const bridgeProviderIdSchema = z
  .string()
  .trim()
  .regex(BRIDGE_PROVIDER_ID, NOT_A_BRIDGE_ID)
  .nullable()

/**
 * The same rule for the Provider Operations form, where **blank stands for `null`** — one field
 * is both the link and the unlink, so it has to accept being emptied. Shared with the
 * wire schema so the rule and its message are authored once (`AGENTS.md`).
 */
export const bridgeProviderIdFieldSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || BRIDGE_PROVIDER_ID.test(value), NOT_A_BRIDGE_ID)

/**
 * Links (or unlinks) a Practitioner to a Bridge provider. Without this mapping a
 * provider-eligibility response names nobody we recognise, so it is the prerequisite for
 * every claimability question we will later ask Bridge.
 *
 * **Super admin only**: a Care Coordinator sees the mapping on the Provider Operations
 * detail page but is refused here whatever the UI shows. The Organization whose Bridge
 * account validates the id is the practitioner's own (derived from their PractitionerRole
 * in `practitioner-ops-access.ts`, the same derivation `getPractitioner` fills the picker
 * from), so the picker and the validator agree without the client naming it. The NPI
 * cross-check is what actually guards the mapping's correctness.
 *
 * `null` unlinks: the identifier is removed and Bridge's `externalId` is cleared
 * best-effort.
 */
export const setPractitionerBridgeProvider = oc
  .route({ method: 'POST', path: '/v2/practitioners/{practitionerId}/bridge-provider' })
  .input(
    z.object({
      practitionerId: z.string().min(1),
      // Disambiguates a target with roles in more than one Organization; never widens access.
      organizationId: z.string().min(1).optional(),
      bridgeProviderId: bridgeProviderIdSchema,
    }),
  )
  .output(
    z.object({
      bridgeProviderId: z.string().nullable(),
      /**
       * Whether Bridge's `externalId` now mirrors our Practitioner id. `false` means the
       * link is saved but one-way — the mapping works, and the mirror can be repaired by
       * saving again.
       */
      externalIdSynced: z.boolean(),
    }),
  )
